import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Chip, Formular, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../../components/meHuellen.js";
import { Meldung, meldungAus } from "../gemeinsam.js";
import { ACCOUNTS_TEXT } from "./texts.js";
import "./accounts.css";

/**
 * GEMEINSAMER BAU der Bereiche „Konten“, „Kostenstellen“, „Vertragsarten“ (Plan Aufgabe 9): eine Liste, die neu lädt,
 * wenn sich ihr Schlüssel ändert; eine Tabelle (`me-tabelle`, Zebra vom Element, Zahlen rechtsbündig); ein Panel
 * (`Arbeitsdialog` rechts angeschlagen, Hausregel „Modalpanel rechts“) mit Formularzuständen. Der Speichern-Knopf ist
 * selbst die Bestätigung (Dirk 216c); gestapelt gefragt wird nur beim Deaktivieren. Nach dem Schreiben wird die Liste
 * neu gelesen — die Ordnung kommt vom Server. Typunabhängig: Zeile und Entwurf sind Parameter.
 */

/** Eine geladene Liste; `reload` liefert die neuen Zeilen. */
export function useList<T>(load: () => Promise<{ items: T[] }>, key: string):
  { rows: T[] | null; error: string | null; reload: () => Promise<T[]> } {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let current = true;
    setRows(null);
    setError(null);
    loadRef.current().then((r) => { if (current) setRows(r.items); },
      (e: unknown) => { if (current) setError(meldungAus(e, ACCOUNTS_TEXT.loadFailed)); });
    return () => { current = false; };
  }, [key]);
  const reload = useCallback(async () => {
    const r = await loadRef.current();
    setRows(r.items);
    return r.items;
  }, []);
  return { rows, error, reload };
}

/** Liste und Entwurf eines Bereichs: `open(null)` legt an, `open(zeile)` bearbeitet. */
export function useMasterData<Row extends { id: string }, Draft extends { id: string | null; active: boolean }>(
  load: () => Promise<{ items: Row[] }>, key: string, toDraft: (row: Row | null) => Draft,
) {
  const list = useList(load, key);
  const [edit, setEdit] = useState<{ draft: Draft; start: Draft } | null>(null);
  const open = (row: Row | null): void => { const d = toDraft(row); setEdit({ draft: d, start: d }); };
  return {
    ...list, open, draft: edit?.draft ?? null, close: () => setEdit(null),
    change: (patch: Partial<Draft>) => setEdit((e) => (e === null ? e : { ...e, draft: { ...e.draft, ...patch } })),
    dirty: edit !== null && JSON.stringify(edit.draft) !== JSON.stringify(edit.start),
    /** Der Entwurf schaltet „aktiv“ ab — nur dann fragt das Panel vor dem Schreiben (216c). */
    deactivating: edit !== null && edit.start.active && !edit.draft.active,
    /** „Neu laden“ nach einem fremden Stand (409): Liste und Entwurf aus dem Serverstand. */
    refresh: async () => {
      const id = edit?.draft.id ?? null;
      const rows = await list.reload();
      open(rows.find((r) => r.id === id) ?? null);
    },
  };
}

/** Kennzeichen (Fläche, ohne Rahmen) „deaktiviert“ und „verwendet“. */
export function StateFlags({ row }: { row: { active: boolean; inUse?: boolean } }): ReactElement {
  return (
    <>
      {!row.active && <Chip>{ACCOUNTS_TEXT.inactive}</Chip>}
      {row.inUse === true && <Chip>{ACCOUNTS_TEXT.inUse}</Chip>}
    </>
  );
}

export type Column<Row> = { head: string; cell: (row: Row) => ReactNode; numeric?: boolean };

const alignRight = (numeric: boolean | undefined) => (numeric === true ? { "data-ausrichtung": "rechts" } : {});

export function MasterDataTable<Row extends { id: string }>(p: {
  rows: Row[]; columns: Column<Row>[]; editName: (row: Row) => string; onEdit: (row: Row) => void;
}): ReactElement {
  return (
    <Tabelle stapeln>
      <table className="vwk__tabelle">
        <thead>
          <tr>
            {p.columns.map((c) => <th key={c.head} scope="col" {...alignRight(c.numeric)}>{c.head}</th>)}
            <th scope="col" aria-label={ACCOUNTS_TEXT.edit} />
          </tr>
        </thead>
        <tbody>
          {p.rows.map((r) => (
            <tr key={r.id}>
              {p.columns.map((c) => (
                <td key={c.head} {...alignRight(c.numeric)}>
                  <span className="vwk__zelle">{c.cell(r)}</span>
                </td>
              ))}
              <td>
                <KiKnopf kompakt name={p.editName(r)} onClick={() => p.onEdit(r)}>{ACCOUNTS_TEXT.edit}</KiKnopf>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Tabelle>
  );
}

/** Lade- und Fehlerzustand eines Bereichs, sonst sein Inhalt. */
export function Loaded<T>(p: { list: { rows: T[] | null; error: string | null }; children: (rows: T[]) => ReactNode }):
  ReactElement {
  if (p.list.error !== null) return <Meldung text={p.list.error} />;
  if (p.list.rows === null) return <p className="vw__hinweis">{ACCOUNTS_TEXT.loading}</p>;
  return <>{p.children(p.list.rows)}</>;
}

/**
 * Das Panel eines Entwurfs. `save` schreibt (wirft den `ApiError`, Feldfehler hängen am Feld); schaltet der Entwurf
 * „aktiv“ ab (`deactivating`), fragt es vorher gestapelt und nennt den Eintrag (`name`). Abbrechen fragt bei Ungespeichertem nach; während des Schreibens ist kein Ausgang offen.
 */
export function MasterDataPanel(p: {
  title: string; name: string; dirty: boolean; deactivating: boolean; save: () => Promise<unknown>;
  /** Die Art vor dem Namen in der Frage („Konto ‹Name› deaktivieren?“) — nur, wo das Panel deaktivieren kann. */
  kind?: string;
  refresh: () => Promise<void>;
  onClose: () => void; labels: Readonly<Record<string, string>>;
  children: (errors: Readonly<Record<string, string | undefined>>) => ReactNode;
}): ReactElement {
  const { bestaetigen } = useBestaetigung();
  const formular = useFormularZustand<true>({
    speichern: async () => { await p.save(); return true; },
    neuLaden: p.refresh, bekannteFelder: Object.keys(p.labels), beschriftungen: p.labels,
  });
  useBeforeUnload(p.dirty);
  const running = formular.laeuft;

  async function save(): Promise<void> {
    if (p.deactivating && !(await bestaetigen({ titel: ACCOUNTS_TEXT.confirmDeactivate(p.kind, p.name),
      text: ACCOUNTS_TEXT.deactivateConsequence, bestaetigenText: ACCOUNTS_TEXT.deactivate }))) return;
    if (await formular.aktion() === true) p.onClose();
  }
  async function cancel(): Promise<void> {
    if (running) return;
    if (await darfAbbrechen(p.dirty)) p.onClose();
  }

  return (
    <Arbeitsdialog titel={p.title} panel="verwaltung-stammdaten" onAbbrechen={cancel} abbrechenGesperrt={running}
      {...(formular.meldung === null ? {} : { meldungen: <Meldung text={formular.meldung} /> })}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={formular.zustand !== "bereit"} onClick={() => { void save(); }}>
            {ACCOUNTS_TEXT.save}
          </KiKnopf>
          <KiKnopf deaktiviert={running} onClick={() => { void cancel(); }}>{ACCOUNTS_TEXT.cancel}</KiKnopf>
        </>
      )}>
      <Formular beschriftung="oben" {...formular.formularProps}>{p.children(formular.feldFehler)}</Formular>
    </Arbeitsdialog>
  );
}

